import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Printer,
  User,
  Paperclip,
  Palmtree,
  AlertCircle,
  RotateCcw,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import SignaturePad from '../components/SignaturePad';
import PanelSaldaUrlopu from '../components/PanelSaldaUrlopu';
import Podpis from '../components/Podpis';
import toast from 'react-hot-toast';
import kadryService from '../services/kadryService';
import { countWorkingDays, countCalendarDays } from '../utils/polishHolidays';
import { formatSignerNameUnderPad } from '../utils/userDisplayName';

const RODZAJE_URLOPU = [
  { value: 'wypoczynkowy', label: 'Wypoczynkowy' },
  { value: 'okolicznosciowy', label: 'Okolicznościowy' },
  { value: 'bezplatny', label: 'Bezpłatny' },
  { value: 'na_zadanie', label: 'Na żądanie' }
];

const fieldInputClass =
  'w-full bg-transparent text-sm font-semibold text-ink placeholder:text-ink-faint placeholder:font-normal focus:outline-none';

const SectionBadge = ({ n }) => (
  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-surface border border-black/[0.06] text-xs font-bold text-ink">
    {n}
  </span>
);

const FieldBox = ({ label, children, className = '' }) => (
  <div className={`rounded-xl border border-gray-200 bg-white px-4 py-2.5 ${className}`}>
    <label className="block text-[11px] font-medium text-ink-faint mb-0.5">{label}</label>
    {children}
  </div>
);

const WniosekUrlopowy = () => {
  const { id: editId } = useParams();
  const isEditMode = !!editId;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const przełożonyZInicjalizacji = useRef(false);
  const fileInputRef = useRef(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [formData, setFormData] = useState({
    pracownikImie: user?.firstName || '',
    pracownikNazwisko: user?.lastName || '',
    stanowisko: user?.position || '',
    dataZlozenia: new Date().toISOString().split('T')[0],
    rodzajUrlopu: 'wypoczynkowy',
    dataOd: '',
    dataDo: '',
    liczbaDni: '',
    telefonKontaktowy: user?.phone || '',
    podpisPracownika: '',
    przełożony: '',
    zalacznik: null,
    opisUrlopu: '',
    wliczWeekendyWDniUrlopowe: false
  });

  const {
    data: kontekstWniosku,
    isLoading: kontekstLoading,
    isError: kontekstError,
    refetch: refetchKontekst
  } = useQuery(
    'wniosek-urlopowy-kontekst-zlozenia',
    kadryService.getKontekstZlozeniaWnioskuUrlopowego,
    { staleTime: 2 * 60 * 1000, retry: 1, enabled: !isEditMode }
  );

  const {
    data: istniejacyWniosekData,
    isLoading: wniosekLoading,
    isError: wniosekError
  } = useQuery(
    ['wniosek-urlopowy', editId],
    () => kadryService.getWniosekUrlopowy(editId),
    { enabled: isEditMode, retry: 1 }
  );

  const istniejacyWniosek = istniejacyWniosekData?.wniosek;
  const wniosekZaladowany = useRef(false);

  useEffect(() => {
    if (!isEditMode || !istniejacyWniosek || wniosekZaladowany.current) return;
    if (!istniejacyWniosek.oczekujeNaPoprawke) return;
    wniosekZaladowany.current = true;

    const formatDateInput = (d) => {
      if (!d) return '';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return '';
      return dt.toISOString().split('T')[0];
    };

    setFormData({
      pracownikImie: istniejacyWniosek.pracownikImie || '',
      pracownikNazwisko: istniejacyWniosek.pracownikNazwisko || '',
      stanowisko: istniejacyWniosek.stanowisko || '',
      dataZlozenia: formatDateInput(istniejacyWniosek.dataZlozenia || istniejacyWniosek.createdAt),
      rodzajUrlopu: istniejacyWniosek.rodzajUrlopu || 'wypoczynkowy',
      dataOd: formatDateInput(istniejacyWniosek.dataOd),
      dataDo: formatDateInput(istniejacyWniosek.dataDo),
      liczbaDni: String(istniejacyWniosek.liczbaDni || ''),
      telefonKontaktowy: istniejacyWniosek.telefonKontaktowy || user?.phone || '',
      podpisPracownika: istniejacyWniosek.podpisPracownika || '',
      przełożony: istniejacyWniosek.przełożony?._id || istniejacyWniosek.przełożony || '',
      zalacznik: null,
      opisUrlopu: istniejacyWniosek.opisUrlopu || '',
      wliczWeekendyWDniUrlopowe: istniejacyWniosek.wliczWeekendyWDniUrlopowe === true
    });
  }, [isEditMode, istniejacyWniosek, user?.phone]);

  // Dane pracownika (pole przełożony ustala kontekst złożenia)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        pracownikImie: user.firstName || prev.pracownikImie || '',
        pracownikNazwisko: user.lastName || prev.pracownikNazwisko || '',
        stanowisko: user.position || prev.stanowisko || '',
        telefonKontaktowy: user.phone || prev.telefonKontaktowy || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!kontekstWniosku || przełożonyZInicjalizacji.current) return;
    przełożonyZInicjalizacji.current = true;
    const id = kontekstWniosku.domyslnyPrzełożonyId || '';
    setFormData(prev => ({ ...prev, przełożony: id }));
  }, [kontekstWniosku]);

  // Saldo urlopu na żywo: przeliczane po każdej zmianie dat, rodzaju i sposobu liczenia dni
  const datyKompletne = Boolean(formData.dataOd && formData.dataDo && formData.dataOd <= formData.dataDo);
  const parametrySalda = {
    dataOd: datyKompletne ? formData.dataOd : '',
    dataDo: datyKompletne ? formData.dataDo : '',
    rodzajUrlopu: formData.rodzajUrlopu,
    wliczWeekendy: formData.wliczWeekendyWDniUrlopowe,
    pominId: isEditMode ? editId : undefined
  };
  const {
    data: saldoData,
    isLoading: saldoLoading,
    isFetching: saldoFetching,
    isError: saldoError
  } = useQuery(
    ['saldo-urlopu', parametrySalda],
    () => kadryService.getSaldoUrlopu(parametrySalda),
    { keepPreviousData: true, staleTime: 30 * 1000, retry: 1 }
  );
  const saldo = saldoData?.saldo;

  // Oblicz liczbę dni: domyślnie dni robocze (bez weekendów i świąt); opcjonalnie wszystkie dni kalendarzowe w okresie
  useEffect(() => {
    if (formData.dataOd && formData.dataDo) {
      const dni = formData.wliczWeekendyWDniUrlopowe
        ? countCalendarDays(formData.dataOd, formData.dataDo)
        : countWorkingDays(formData.dataOd, formData.dataDo);
      setFormData(prev => ({ ...prev, liczbaDni: dni.toString() }));
    }
  }, [formData.dataOd, formData.dataDo, formData.wliczWeekendyWDniUrlopowe]);

  // Mutacja zapisywania wniosku (akceptuje FormData lub obiekt)
  const saveWniosekMutation = useMutation(
    (data) => (isEditMode
      ? kadryService.updateWniosekUrlopowy(editId, data)
      : kadryService.createWniosekUrlopowy(data)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('wnioski-urlopowe');
        queryClient.invalidateQueries('wnioski-do-podpisania');
        if (isEditMode) queryClient.invalidateQueries(['wniosek-urlopowy', editId]);
        toast.success(isEditMode ? 'Wniosek został poprawiony i przesłany ponownie' : 'Wniosek urlopowy został złożony pomyślnie');
        window.location.replace(`${window.location.origin}/kadry/wnioski?tab=moje`);
      },
      onError: (error) => {
        setIsSubmitted(false);
        const msg = error.response?.data?.message || error.response?.data?.errors?.[0] || 'Wystąpił błąd podczas składania wniosku';
        toast.error(msg);
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Blokuj wielokrotne wysyłanie
    if (isSubmitted || saveWniosekMutation.isLoading) {
      return;
    }

    // Walidacja
    if (!formData.pracownikImie || !formData.pracownikNazwisko) {
      toast.error('Proszę wypełnić imię i nazwisko');
      return;
    }
    if (!formData.dataZlozenia) {
      toast.error('Proszę podać datę złożenia');
      return;
    }
    if (!formData.dataOd || !formData.dataDo) {
      toast.error('Proszę wybrać okres urlopu');
      return;
    }
    if (new Date(formData.dataOd) > new Date(formData.dataDo)) {
      toast.error('Data rozpoczęcia nie może być późniejsza niż data zakończenia');
      return;
    }
    const dniNum = parseInt(String(formData.liczbaDni || '0'), 10);
    if (!Number.isFinite(dniNum) || dniNum < 1) {
      toast.error('Liczba dni urlopu musi wynosić co najmniej 1 — sprawdź okres');
      return;
    }
    if (!formData.podpisPracownika) {
      toast.error('Proszę podpisać wniosek');
      return;
    }
    if (!formData.przełożony) {
      toast.error('Wskaż osobę, do której trafi wniosek do podpisania.');
      return;
    }

    if (isEditMode && istniejacyWniosek && !istniejacyWniosek.oczekujeNaPoprawke) {
      toast.error('Ten wniosek nie oczekuje na poprawę');
      return;
    }

    if (!isEditMode && kontekstWniosku?.glownyNaUrlopie && (kontekstWniosku.listaZarzadu || []).length === 0) {
      toast.error(
        'Przełożony główny jest na urlopie, a brak dostępnej osoby ze stanowiska ZARZĄD. Skontaktuj się z HR.'
      );
      return;
    }

    if (saldo?.przekroczenie) {
      const ok = window.confirm(
        `${saldo.ostrzezenia.join('\n')}\n\nWniosek przekracza dostępny urlop. Czy mimo to chcesz go złożyć?`
      );
      if (!ok) return;
    }

    setIsSubmitted(true);
    const przełożonyId = typeof formData.przełożony === 'object' && formData.przełożony?._id
      ? formData.przełożony._id
      : (formData.przełożony || '');

    const buildPayload = (zalacznikData) => ({
      pracownikImie: (formData.pracownikImie || user?.firstName || '').trim(),
      pracownikNazwisko: (formData.pracownikNazwisko || user?.lastName || '').trim(),
      stanowisko: formData.stanowisko || '',
      dataZlozenia: formData.dataZlozenia,
      rodzajUrlopu: formData.rodzajUrlopu,
      dataOd: formData.dataOd,
      dataDo: formData.dataDo,
      liczbaDni: formData.liczbaDni || '1',
      telefonKontaktowy: formData.telefonKontaktowy || '',
      podpisPracownika: formData.podpisPracownika,
      ...(isEditMode ? {} : { przełożony: przełożonyId }),
      wliczWeekendyWDniUrlopowe: formData.wliczWeekendyWDniUrlopowe === true,
      ...(formData.opisUrlopu && String(formData.opisUrlopu).trim() !== ''
        ? { opisUrlopu: String(formData.opisUrlopu).trim() }
        : {}),
      ...(zalacznikData && { załącznikUrl: zalacznikData.filename, załącznikNazwa: zalacznikData.originalName })
    });

    const hasFile = formData.zalacznik && formData.zalacznik instanceof File && formData.zalacznik.size > 0;

    if (hasFile) {
      try {
        const zalacznikData = await kadryService.uploadZalacznikWniosek(formData.zalacznik);
        saveWniosekMutation.mutate(buildPayload(zalacznikData));
      } catch (err) {
        setIsSubmitted(false);
        toast.error(err.response?.data?.message || 'Błąd przesyłania załącznika');
      }
    } else {
      saveWniosekMutation.mutate(buildPayload(null));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleFiles = (files) => {
    const file = files?.[0];
    if (file) setFormData(prev => ({ ...prev, zalacznik: file }));
  };

  const handleResetForm = () => {
    if (!window.confirm('Czy na pewno chcesz wyczyścić formularz? Wprowadzone dane zostaną utracone.')) return;
    setFormData({
      pracownikImie: user?.firstName || '',
      pracownikNazwisko: user?.lastName || '',
      stanowisko: user?.position || '',
      dataZlozenia: new Date().toISOString().split('T')[0],
      rodzajUrlopu: 'wypoczynkowy',
      dataOd: '',
      dataDo: '',
      liczbaDni: '',
      telefonKontaktowy: user?.phone || '',
      podpisPracownika: '',
      przełożony: kontekstWniosku?.domyslnyPrzełożonyId || '',
      zalacznik: null,
      opisUrlopu: '',
      wliczWeekendyWDniUrlopowe: false
    });
  };

  if (isEditMode && wniosekLoading) {
    return (
      <div className="card p-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditMode && (wniosekError || (istniejacyWniosek && !istniejacyWniosek.oczekujeNaPoprawke))) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-orange-500 mb-4" />
        <h2 className="text-lg font-semibold text-ink mb-2">Nie można edytować tego wniosku</h2>
        <p className="text-ink-soft mb-4">
          {wniosekError ? 'Nie udało się wczytać wniosku.' : 'Ten wniosek nie oczekuje na poprawę.'}
        </p>
        <Link to="/kadry/wnioski?tab=moje" className="text-primary-600 hover:underline">Wróć do moich wniosków</Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl mx-auto print:max-w-none">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-xs text-ink-faint print:hidden">
        <Link to="/kadry/wnioski?tab=moje" className="hover:text-ink">Wnioski urlopowe</Link>
        <span>/</span>
        <span className="text-ink font-medium">{isEditMode ? 'Edycja wniosku' : 'Nowy wniosek'}</span>
      </div>

      {/* Nagłówek */}
      <div className="card px-6 py-5 print:hidden">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/kadry/dokumenty"
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              title="Powrót do dokumentów"
            >
              <ArrowLeft className="h-5 w-5 text-ink-soft" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-ink">
                {isEditMode ? 'Popraw wniosek urlopowy' : 'Wniosek urlopowy'}
              </h1>
              <p className="text-sm text-ink-faint mt-0.5">
                {isEditMode
                  ? 'Wprowadź poprawki zgodnie z uwagami przełożonego i prześlij wniosek ponownie'
                  : 'Wypełnij formularz wniosku urlopowego'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handlePrint}
            className="btn-secondary flex items-center gap-2 flex-shrink-0"
          >
            <Printer className="h-4 w-4" />
            <span>Drukuj</span>
          </button>
        </div>
      </div>

      {/* Druk: nagłówek dokumentu */}
      <div className="hidden print:block text-center mb-4 border-b-2 border-gray-300 pb-4">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">WNIOSEK URLOPOWY</h2>
        <p className="text-sm text-gray-800">
          Data złożenia:{' '}
          {formData.dataZlozenia
            ? new Date(`${formData.dataZlozenia}T12:00:00`).toLocaleDateString('pl-PL')
            : '—'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 print:space-y-2">
        {isEditMode && istniejacyWniosek?.aktywnyKomentarzZwrotu?.komentarz && (
          <div className="card px-5 py-4 bg-orange-50 border-orange-200 print:hidden">
            <p className="text-sm font-medium text-orange-900 flex items-center gap-1.5">
              <AlertCircle className="h-4 w-4" />
              Uwagi przełożonego
            </p>
            <p className="text-sm text-orange-900 mt-2 whitespace-pre-wrap">{istniejacyWniosek.aktywnyKomentarzZwrotu.komentarz}</p>
          </div>
        )}

        {/* 1. Dane pracownika */}
        <div className="card px-6 py-5 print:shadow-none print:border-0 print:break-inside-avoid">
          <div className="flex items-center gap-2.5 mb-4">
            <SectionBadge n={1} />
            <h3 className="text-base font-semibold text-ink flex items-center gap-2">
              <User className="h-4 w-4 text-primary-600" />
              Dane pracownika
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldBox label="Imię *">
              <input
                type="text"
                required
                value={formData.pracownikImie}
                onChange={(e) => setFormData({ ...formData, pracownikImie: e.target.value })}
                className={fieldInputClass}
                placeholder="Jan"
              />
            </FieldBox>
            <FieldBox label="Nazwisko *">
              <input
                type="text"
                required
                value={formData.pracownikNazwisko}
                onChange={(e) => setFormData({ ...formData, pracownikNazwisko: e.target.value })}
                className={fieldInputClass}
                placeholder="Kowalski"
              />
            </FieldBox>
            <FieldBox label="Stanowisko">
              <input
                type="text"
                value={formData.stanowisko}
                onChange={(e) => setFormData({ ...formData, stanowisko: e.target.value })}
                className={fieldInputClass}
                placeholder="Specjalista"
              />
            </FieldBox>
            <FieldBox label="Telefon kontaktowy">
              <input
                type="tel"
                value={formData.telefonKontaktowy}
                onChange={(e) => setFormData({ ...formData, telefonKontaktowy: e.target.value })}
                className={fieldInputClass}
                placeholder="+48 123 456 789"
              />
            </FieldBox>
          </div>
        </div>

        {/* 2. Rodzaj urlopu */}
        <div className="card px-6 py-5 print:shadow-none print:border-0 print:break-inside-avoid">
          <div className="flex items-center gap-2.5 mb-4">
            <SectionBadge n={2} />
            <h3 className="text-base font-semibold text-ink">Rodzaj urlopu</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {RODZAJE_URLOPU.map((rodzaj) => {
              const checked = formData.rodzajUrlopu === rodzaj.value;
              return (
                <label
                  key={rodzaj.value}
                  className={`flex items-center gap-2.5 rounded-xl border-2 px-3.5 py-3 cursor-pointer transition-colors print:border ${
                    checked ? 'border-ink bg-primary-50/70' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="rodzajUrlopu"
                    value={rodzaj.value}
                    checked={checked}
                    onChange={(e) => setFormData({ ...formData, rodzajUrlopu: e.target.value })}
                    className="sr-only"
                  />
                  <span className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${checked ? 'border-ink' : 'border-gray-300'}`}>
                    {checked && <span className="h-2 w-2 rounded-full bg-ink" />}
                  </span>
                  <span className="text-sm font-medium text-ink">{rodzaj.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* 3. Okres urlopu */}
        <div className="card px-6 py-5 print:shadow-none print:border-0 print:break-inside-avoid">
          <div className="flex items-center gap-2.5 mb-4">
            <SectionBadge n={3} />
            <h3 className="text-base font-semibold text-ink">Okres urlopu</h3>
          </div>

          <div className="mb-4 rounded-xl border border-gray-200 bg-surface px-4 py-3 print:hidden">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={formData.wliczWeekendyWDniUrlopowe}
                onChange={(e) =>
                  setFormData({ ...formData, wliczWeekendyWDniUrlopowe: e.target.checked })
                }
                className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-ink">
                <span className="font-medium">Tygodniowy wymiar pracy obejmuje weekendy / święta</span>
                <span className="block text-ink-faint mt-0.5">
                  Zaznacz, jeśli z umowy wynika obowiązek pracy także w soboty, niedziele lub święta — dni urlopu
                  liczone są wtedy jak pełne dni kalendarzowe w wybranym okresie (nadal odliczane od puli urlopu).
                </span>
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <FieldBox label="Data złożenia wniosku *" className="print:hidden">
              <input
                id="data-zlozenia-wniosku"
                type="date"
                required
                value={formData.dataZlozenia}
                onChange={(e) => setFormData({ ...formData, dataZlozenia: e.target.value })}
                className={fieldInputClass}
              />
            </FieldBox>
            <FieldBox label="Data od *">
              <input
                type="date"
                required
                value={formData.dataOd}
                onChange={(e) => {
                  const newDataOd = e.target.value;
                  setFormData(prev => {
                    const updated = { ...prev, dataOd: newDataOd };
                    if (prev.dataDo && newDataOd && new Date(prev.dataDo) < new Date(newDataOd)) {
                      updated.dataDo = '';
                      updated.liczbaDni = '';
                    }
                    return updated;
                  });
                }}
                max={formData.dataDo || undefined}
                className={fieldInputClass}
              />
            </FieldBox>
            <FieldBox label="Data do *">
              <input
                type="date"
                required
                value={formData.dataDo}
                onChange={(e) => {
                  const newDataDo = e.target.value;
                  if (formData.dataOd && newDataDo && new Date(newDataDo) < new Date(formData.dataOd)) {
                    toast.error('Data zakończenia nie może być wcześniejsza niż data rozpoczęcia');
                    return;
                  }
                  setFormData({ ...formData, dataDo: newDataDo });
                }}
                min={formData.dataOd || undefined}
                className={fieldInputClass}
              />
            </FieldBox>
            <FieldBox label="Liczba dni" className="!bg-primary-50/70 !border-primary-200">
              <input
                type="number"
                readOnly
                value={formData.liczbaDni || ''}
                className={fieldInputClass}
                placeholder="Obliczane automatycznie"
              />
            </FieldBox>
          </div>
          <p className="mt-2 text-xs text-ink-faint">
            {formData.wliczWeekendyWDniUrlopowe
              ? 'Uwzględniane są wszystkie dni od–do (weekendy i święta w okresie liczą się do puli).'
              : 'Uwzględniane są dni robocze; sobota, niedziela i święta państwowe są pomijane.'}
          </p>

          <div className="mt-4 print:hidden">
            <PanelSaldaUrlopu
              saldo={saldo}
              ladowanie={saldoLoading || saldoFetching}
              blad={saldoError}
            />
          </div>

          <div className="mt-4">
            <label htmlFor="opis-urlopu" className="block text-sm font-medium text-ink mb-1.5">
              Opis urlopu <span className="text-ink-faint font-normal">(opcjonalnie)</span>
            </label>
            <textarea
              id="opis-urlopu"
              rows={3}
              maxLength={1000}
              value={formData.opisUrlopu}
              onChange={(e) => setFormData({ ...formData, opisUrlopu: e.target.value })}
              placeholder="Np. wyjazd rodzinny, sprawy osobiste…"
              className="input resize-y min-h-[5rem]"
            />
            <p className="mt-1 text-xs text-ink-faint text-right">{(formData.opisUrlopu || '').length} / 1000</p>
          </div>
        </div>

        {/* 4. Załączniki */}
        <div className="card px-6 py-5 print:hidden">
          <div className="flex items-center gap-2.5 mb-4">
            <SectionBadge n={4} />
            <h3 className="text-base font-semibold text-ink">
              Załączniki <span className="text-ink-faint font-normal text-sm">(opcjonalnie)</span>
            </h3>
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors ${
              isDragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-surface hover:border-gray-300'
            }`}
          >
            <div className="p-2.5 rounded-full bg-white border border-gray-200">
              <Paperclip className="h-5 w-5 text-ink-soft" />
            </div>
            <div>
              <p className="text-sm font-medium text-ink">Przeciągnij pliki tutaj lub kliknij, aby dodać</p>
              <p className="text-xs text-ink-faint mt-1">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF (max. 10 MB)</p>
            </div>
            <span className="mt-1 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg border border-gray-300 bg-white text-xs font-semibold text-ink">
              <Paperclip className="h-3.5 w-3.5" />
              Wybierz pliki
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
            />
          </div>
          {formData.zalacznik && (
            <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-primary-50 border border-primary-100 px-3 py-2">
              <span className="flex items-center gap-2 text-sm text-ink min-w-0">
                <Paperclip className="h-4 w-4 text-primary-600 flex-shrink-0" />
                <span className="truncate">{formData.zalacznik.name}</span>
              </span>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, zalacznik: null }))}
                className="text-ink-faint hover:text-red-600 flex-shrink-0"
                title="Usuń załącznik"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* 5. Przekaż do podpisania */}
        <div className="card px-6 py-5 print:shadow-none print:border-0 print:break-inside-avoid">
          <div className="flex items-center gap-2.5 mb-4">
            <SectionBadge n={5} />
            <h3 className="text-base font-semibold text-ink">Przekaż do podpisania</h3>
          </div>

          <div className="print:hidden">
            {kontekstLoading && (
              <div className="flex items-center gap-3 px-4 py-6 bg-surface border border-gray-200 rounded-xl">
                <LoadingSpinner size="sm" />
                <span className="text-sm text-ink-soft">Sprawdzanie przełożonego głównego i dostępności…</span>
              </div>
            )}

            {!kontekstLoading && kontekstError && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
                Nie udało się załadować danych wniosku.{' '}
                <button type="button" onClick={() => refetchKontekst()} className="font-medium underline">
                  Odśwież
                </button>
              </div>
            )}

            {!kontekstLoading && !kontekstError && kontekstWniosku && !kontekstWniosku.maPrzełożonego && (
              <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-amber-800 font-medium flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4" />
                  Brak przełożonego głównego w profilu
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Administrator musi ustawić pole „Przełożony główny” przy Twoim koncie (edycja użytkownika).
                </p>
                <Link to="/profile" className="inline-block mt-2 text-sm font-medium text-amber-800 hover:text-amber-900 underline">
                  Dowiedz się więcej →
                </Link>
              </div>
            )}

            {!kontekstLoading && !kontekstError && kontekstWniosku?.maPrzełożonego && !kontekstWniosku.glownyNaUrlopie && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-ink mb-1.5">
                  Przełożony główny (z profilu)
                </label>
                <div className="px-4 py-3 bg-surface border border-gray-200 rounded-xl">
                  <p className="font-medium text-ink">
                    {kontekstWniosku.glownyPrzełożony?.firstName && kontekstWniosku.glownyPrzełożony?.lastName
                      ? `${kontekstWniosku.glownyPrzełożony.firstName} ${kontekstWniosku.glownyPrzełożony.lastName}`
                      : kontekstWniosku.glownyPrzełożony?.username || 'Przełożony główny'}
                  </p>
                  {kontekstWniosku.glownyPrzełożony?.email && (
                    <p className="text-sm text-ink-faint mt-0.5">{kontekstWniosku.glownyPrzełożony.email}</p>
                  )}
                  <p className="text-xs text-ink-faint mt-2">
                    Wniosek trafia do przełożonego głównego przypisanego w systemie.
                  </p>
                </div>
              </div>
            )}

            {!kontekstLoading && !kontekstError && kontekstWniosku?.maPrzełożonego && kontekstWniosku.glownyNaUrlopie && (
              <div className="mb-4 space-y-4">
                <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-2">
                    <Palmtree className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" aria-hidden />
                    <div>
                      <p className="font-medium text-amber-900">Twój przełożony główny jest na urlopie</p>
                      <p className="text-sm text-amber-800 mt-1 flex flex-wrap items-center gap-2">
                        <span>
                          {kontekstWniosku.glownyPrzełożony?.firstName}{' '}
                          {kontekstWniosku.glownyPrzełożony?.lastName}
                        </span>
                        <span
                          className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 bg-amber-100 px-2 py-0.5 rounded"
                          title="Według zatwierdzonego wniosku urlopowego w systemie"
                        >
                          <AlertCircle className="h-3.5 w-3.5" />
                          na urlopie
                        </span>
                      </p>
                      {kontekstWniosku.urlopGlownegoDo && (
                        <p className="text-xs text-amber-700 mt-1">
                          Do ok. {new Date(kontekstWniosku.urlopGlownegoDo).toLocaleDateString('pl-PL')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-sm text-ink-soft">
                  Wybierz osobę ze <strong>stanowiska {kontekstWniosku.oznaczenieZarzadu || 'ZARZĄD'}</strong> (pole
                  „Stanowisko” w profilu użytkownika), która przyjmie wniosek zastępczo:
                </p>

                {(kontekstWniosku.listaZarzadu || []).length === 0 ? (
                  <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
                    Brak dostępnych osób ze stanowiska ZARZĄD (wszyscy mogą być na urlopie albo nikt nie ma tego
                    stanowiska w systemie). Skontaktuj się z HR.
                  </div>
                ) : (
                  <div>
                    <label htmlFor="przełożony-zarzad-select" className="block text-sm font-medium text-ink mb-1.5">
                      Osoba z zarządu (stanowisko ZARZĄD) *
                    </label>
                    <select
                      id="przełożony-zarzad-select"
                      required
                      value={formData.przełożony}
                      onChange={(e) => setFormData({ ...formData, przełożony: e.target.value })}
                      className="input max-w-lg"
                    >
                      <option value="">— Wybierz —</option>
                      {(kontekstWniosku.listaZarzadu || []).map((z) => (
                        <option key={z._id} value={z._id}>
                          {z.firstName && z.lastName ? `${z.firstName} ${z.lastName}` : z.username}
                          {z.email ? ` · ${z.email}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Podsumowanie wniosku */}
          <div className="rounded-xl bg-primary-50/70 border border-primary-100 px-4 py-3.5">
            <p className="text-sm text-ink leading-relaxed">
              Zwracam się z uprzejmą prośbą o udzielenie urlopu wypoczynkowego w wymiarze{' '}
              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-white border border-primary-200 font-semibold text-ink mx-1">
                {formData.liczbaDni || '—'} dni
              </span>
              w terminie{' '}
              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-white border border-primary-200 font-semibold text-ink mx-1">
                {formData.dataOd ? new Date(formData.dataOd).toLocaleDateString('pl-PL') : '................'}
                {' '}–{' '}
                {formData.dataDo ? new Date(formData.dataDo).toLocaleDateString('pl-PL') : '................'}
              </span>
            </p>
            {formData.opisUrlopu && String(formData.opisUrlopu).trim() !== '' && (
              <p className="text-sm text-ink mt-3 whitespace-pre-wrap">
                <span className="font-semibold">Opis urlopu: </span>
                {String(formData.opisUrlopu).trim()}
              </p>
            )}
            {formData.wliczWeekendyWDniUrlopowe && (
              <p className="text-xs text-ink-soft mt-3">
                Liczba dni powyżej obejmuje wszystkie dni kalendarzowe w podanym terminie (włącznie z weekendami i dniami ustawowo wolnymi),
                zgodnie z zaznaczoną opcją przy składaniu wniosku.
              </p>
            )}
          </div>

          {/* Podpisy */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-ink mb-2">Podpis pracownika *</p>
              <div className="print:hidden">
                <SignaturePad
                  variant="compact"
                  onSignatureChange={(signature) => setFormData({ ...formData, podpisPracownika: signature })}
                  width={360}
                  height={130}
                  signature={formData.podpisPracownika || undefined}
                  signerName={formatSignerNameUnderPad(formData.podpisPracownika, undefined, user)}
                />
              </div>
              {formData.podpisPracownika && (
                <div className="hidden print:block text-center">
                  <Podpis
                    value={formData.podpisPracownika}
                    alt="Podpis pracownika"
                    className="max-w-full h-20 object-contain mx-auto"
                  />
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {[formData.pracownikImie, formData.pracownikNazwisko].filter(Boolean).join(' ').trim()}
                  </p>
                </div>
              )}
              {!formData.podpisPracownika && (
                <div className="print:block hidden h-16 border-b-2 border-gray-400"></div>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-ink mb-2">Podpis przełożonego</p>
              <div
                className="rounded-xl border-2 border-dashed border-gray-200 bg-surface flex items-center justify-center text-center px-4 print:hidden"
                style={{ height: 130 }}
              >
                <span className="text-xs text-ink-faint">Do wypełnienia przez przełożonego</span>
              </div>
              <div className="hidden print:block h-16 border-b-2 border-gray-400"></div>
            </div>
          </div>
        </div>

        {/* Przyciski akcji */}
        <div className="flex items-center justify-between gap-4 flex-wrap print:hidden">
          <button
            type="button"
            onClick={handleResetForm}
            className="flex items-center gap-1.5 text-sm font-medium text-ink-faint hover:text-ink transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Wyczyść formularz
          </button>
          <div className="flex items-center gap-3">
            <Link
              to="/kadry/dokumenty"
              className="btn-secondary"
            >
              Anuluj
            </Link>
            <button
              type="submit"
              disabled={
                isSubmitted ||
                saveWniosekMutation.isLoading ||
                (!isEditMode && !formData.przełożony) ||
                (!isEditMode && kontekstLoading) ||
                (!isEditMode && kontekstError) ||
                (!isEditMode && kontekstWniosku &&
                  kontekstWniosku.maPrzełożonego &&
                  kontekstWniosku.glownyNaUrlopie &&
                  (kontekstWniosku.listaZarzadu || []).length === 0)
              }
              className="btn-primary flex items-center gap-2"
            >
              {saveWniosekMutation.isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Zapisywanie...</span>
                </>
              ) : isSubmitted ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Przekierowywanie...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>{isEditMode ? 'Wyślij poprawiony wniosek' : 'Złóż wniosek'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default WniosekUrlopowy;
